import { useContext, useState } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { GroupEntryViewsProvider } from '~/domains/group/components/GroupEntryViewsProvider/GroupEntryViewsProvider';

import MyEntriesLayout from '../MyEntriesLayout';

import useMyContestEntries from './useMyContestEntries';
import MyEntriesContent from './MyEntriesContent';
import MyEntriesModal from './MyEntriesModal';

type MyEntriesPlayersProps = {
  contestId: string;
  fullContest: FullContest;
  tiersCount?: number;
  myEntriesStatus?: string;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

const ITEMS_COUNT = 3;

function MyEntriesPlayers({
  contestId,
  tiersCount,
  myEntriesStatus,
  fullContest,
  openEntryNameEditModal,
}: MyEntriesPlayersProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(UserContext);

  const { entries, entriesForCurrentUser, slateStatus, isLoading, isError, slateId } =
    useMyContestEntries({
      contestId,
      scheduleId: fullContest?.schedule_id,
      userId: user?.id,
      pageSize: ITEMS_COUNT,
    });

  return (
    <MyEntriesLayout
      myEntriesStatus={myEntriesStatus}
      isLoading={isLoading}
      isError={isError}
      userEntries={entriesForCurrentUser}
      handleAllViewClick={() => setIsModalOpen(true)}
      fullContest={fullContest}
    >
      {user?.id ? (
        <GroupEntryViewsProvider
          entryIds={entries?.map(({ id }) => id) ?? []}
          userId={user.id}
        >
          <MyEntriesContent
            contestId={contestId}
            entries={entries}
            slateId={slateId}
            slateStatus={slateStatus}
            leagueAlias={fullContest?.league?.alias}
            tiersCount={tiersCount}
            isEntertainmentOnly={fullContest?.settings?.entertainmentOnlySettings?.enabled}
            openEntryNameEditModal={openEntryNameEditModal}
          />
        </GroupEntryViewsProvider>
      ) : (
        <MyEntriesContent
          contestId={contestId}
          entries={entries}
          slateId={slateId}
          slateStatus={slateStatus}
          leagueAlias={fullContest?.league?.alias}
          tiersCount={tiersCount}
          isEntertainmentOnly={fullContest?.settings?.entertainmentOnlySettings?.enabled}
          openEntryNameEditModal={openEntryNameEditModal}
        />
      )}

      <MyEntriesModal
        contestId={contestId}
        myEntriesStatus={myEntriesStatus}
        scheduleId={fullContest?.schedule_id}
        userId={user?.id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tiersCount={tiersCount}
        leagueAlias={fullContest?.league?.alias}
        initialTotal={entriesForCurrentUser?.total}
        openEntryNameEditModal={openEntryNameEditModal}
      />
    </MyEntriesLayout>
  );
}

export default MyEntriesPlayers;
